import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormContext } from '../context/FormContext';
import api from '../api';
import useSpeechRecognition from '../hooks/useSpeechRecognition';
import useSpeechSynthesis from '../hooks/useSpeechSynthesis';
import './AgentChat.css';

/**
 * Composant AgentChat - Interface de chat avec l'agent IA Archi_Nocibé
 * Permet à l'utilisateur de communiquer en langage naturel avec l'application
 */
const AgentChat = () => {
  const { setSalarieFormData } = useContext(FormContext);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      text: "Bonjour ! Je suis Archi_Nocibé, votre assistant pour le planning Nocibé. Comment puis-je vous aider ?",
      user: "Archi_Nocibé",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Hooks pour la reconnaissance et synthèse vocale
  const handleSpeechResult = (transcript) => {
    // Le transcript final sera géré par le useEffect qui met à jour inputValue
    // L'envoi automatique sera géré par le useEffect qui détecte la fin de l'écoute
  };

  const handleSpeechError = (error) => {
    // Afficher l'erreur dans le chat
    const errorMessage = {
      text: `⚠️ ${error}`,
      user: "Archi_Nocibé",
      timestamp: new Date(),
      isError: true
    };
    setMessages(prev => [...prev, errorMessage]);
  };

  const {
    isListening,
    transcript,
    isSupported: isSpeechSupported,
    permissionDenied,
    startListening,
    stopListening,
  } = useSpeechRecognition(handleSpeechResult, handleSpeechError);

  const { speak, stop: stopSpeaking, isSpeaking } = useSpeechSynthesis();

  // Référence pour suivre si on doit envoyer automatiquement après la fin de l'écoute
  const shouldAutoSendRef = useRef(false);
  const lastTranscriptRef = useRef('');

  // Mettre à jour le champ de saisie avec le transcript en temps réel
  useEffect(() => {
    if (transcript) {
      setInputValue(transcript);
      lastTranscriptRef.current = transcript;
    }
  }, [transcript]);

  // Marquer qu'on doit envoyer automatiquement quand l'écoute commence
  useEffect(() => {
    if (isListening) {
      shouldAutoSendRef.current = true;
      lastTranscriptRef.current = '';
    }
  }, [isListening]);

  // Détecter quand l'écoute se termine pour envoyer automatiquement
  useEffect(() => {
    if (!isListening && shouldAutoSendRef.current && lastTranscriptRef.current.trim() && !isLoading) {
      // L'écoute vient de se terminer et on a un texte transcrit
      shouldAutoSendRef.current = false; // Réinitialiser le flag
      const textToSend = lastTranscriptRef.current.trim();
      
      // Envoyer automatiquement le message transcrit après un court délai
      const timer = setTimeout(() => {
        if (textToSend) {
          handleSendMessageWithText(textToSend);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isListening, isLoading]);

  // Charger les notifications au démarrage
  useEffect(() => {
    loadNotifications();
  }, []);

  // Scroll automatique vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus sur l'input quand le chat s'ouvre
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  /**
   * Charge les notifications proactives depuis l'API
   */
  const loadNotifications = async () => {
    try {
      const response = await api.get('/notifications/');
      const notifications = response.data;
      
      if (notifications && notifications.length > 0) {
        // Ajouter les notifications comme messages d'ARCHI
        const notificationMessages = notifications.map(notif => ({
          text: notif.message,
          user: "Archi_Nocibé",
          timestamp: new Date(notif.date_creation),
          isNotification: true,
          notificationId: notif.id,
          type: notif.type_notification,
          priorite: notif.priorite
        }));
        
        // Ajouter après le message de bienvenue
        setMessages(prev => [
          prev[0],
          ...notificationMessages,
          ...prev.slice(1)
        ]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
    }
  };

  /**
   * Marque une notification comme lue
   */
  const markNotificationAsRead = async (notificationId) => {
    try {
      await api.patch('/notifications/', { id: notificationId });
    } catch (error) {
      console.error('Erreur lors de la marque de la notification comme lue:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * Envoie un message avec un texte spécifique (utilisé pour l'envoi automatique après reconnaissance vocale)
   */
  const handleSendMessageWithText = async (textToSend) => {
    if (!textToSend || !textToSend.trim() || isLoading) {
      return;
    }

    const userMessage = textToSend.trim();
    
    // Ajouter le message de l'utilisateur à l'historique
    const newUserMessage = {
      text: userMessage,
      user: "me",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Préparer l'historique de conversation (3 derniers messages)
      // Format: [{"role": "user", "content": "..."}, {"role": "assistant", "content": "..."}]
      const historique = messages
        .slice(-3) // Prendre les 3 derniers messages
        .filter(msg => msg.user !== "Archi_Nocibé" || msg.text !== "Bonjour ! Je suis Archi_Nocibé, votre assistant pour le planning Nocibé. Comment puis-je vous aider ?") // Exclure le message d'accueil
        .map(msg => ({
          role: msg.user === "me" ? "user" : "assistant",
          content: msg.text
        }));

      // Appel à l'API pour interpréter la commande avec l'historique
      const response = await api.post('/agent/command/', {
        commande: userMessage,
        historique: historique
      });

      const data = response.data;

      // Nouvelle logique pour détecter les différentes actions
      if (data.action_data && data.action_data.type === 'demander_clarification') {
        // Action de clarification : afficher la question
        const clarificationData = data.action_data;
        const newIAMessage = {
          text: clarificationData.message || clarificationData.question || 'J\'ai besoin de plus d\'informations.',
          user: "Archi_Nocibé",
          timestamp: new Date(),
          rawData: clarificationData,
          isClarification: true
        };
        setMessages(prev => [...prev, newIAMessage]);
        
        // Lire la réponse à voix haute si la voix est activée
        if (voiceEnabled && (clarificationData.message || clarificationData.question)) {
          speak(clarificationData.message || clarificationData.question);
        }
      } else if (data.action_data && data.action_data.type === 'generer_planning') {
        // Action de génération de planning
        const planningData = data.action_data;
        const newIAMessage = {
          text: planningData.message || 'Planning généré avec succès.',
          user: "Archi_Nocibé",
          timestamp: new Date(),
          rawData: planningData
        };
        setMessages(prev => [...prev, newIAMessage]);
        
        // Lire la réponse à voix haute si la voix est activée
        if (voiceEnabled && planningData.message) {
          speak(planningData.message);
        }
      } else if (data.action_data && data.action_data.type === 'consulter_stats') {
        // Action de consultation des statistiques
        const statsData = data.action_data;
        const newIAMessage = {
          text: statsData.message || 'Statistiques consultées.',
          user: "Archi_Nocibé",
          timestamp: new Date(),
          rawData: statsData
        };
        setMessages(prev => [...prev, newIAMessage]);
        
        // Lire la réponse à voix haute si la voix est activée
        if (voiceEnabled && statsData.message) {
          speak(statsData.message);
        }
      } else if (data.action_data && data.action_data.type === 'analyser_situation') {
        // Action d'analyse de situation
        const analyseData = data.action_data;
        const newIAMessage = {
          text: analyseData.message || 'Analyse effectuée.',
          user: "Archi_Nocibé",
          timestamp: new Date(),
          rawData: analyseData
        };
        setMessages(prev => [...prev, newIAMessage]);
        
        // Lire la réponse à voix haute si la voix est activée
        if (voiceEnabled && analyseData.message) {
          speak(analyseData.message);
        }
      } else if (data.action_data && data.action_data.type === 'pre_remplir_formulaire_salarie') {
        const actionData = data.action_data;

        // Met à jour l'état global du formulaire
        setSalarieFormData(prevData => ({
          ...prevData,
          nom: actionData.nom_salarie || '', // Utilise la valeur de l'IA ou vide
          // Utilise profil_id si disponible, sinon cherche le profil par nom
          profil: actionData.profil_id ? actionData.profil_id.toString() : '',
        }));

        // Redirige l'utilisateur vers la page de gestion de l'équipe
        navigate('/admin');

        // Ajoute le message de confirmation au chat
        const newIAMessage = {
          text: actionData.message,
          user: "Archi_Nocibé",
          timestamp: new Date(),
          rawData: actionData
        };
        setMessages(prev => [...prev, newIAMessage]);
        
        // Lire la réponse à voix haute si la voix est activée
        if (voiceEnabled && actionData.message) {
          speak(actionData.message);
        }
      } else if (data.action_data) {
        // Autre type d'action_data (non pré-remplissage)
        const newIAMessage = {
          text: data.action_data.message || 'Action exécutée avec succès.',
          user: "Archi_Nocibé",
          timestamp: new Date(),
          rawData: data.action_data
        };
        setMessages(prev => [...prev, newIAMessage]);
        
        // Lire la réponse à voix haute si la voix est activée
        if (voiceEnabled && (data.action_data.message || 'Action exécutée avec succès.')) {
          speak(data.action_data.message || 'Action exécutée avec succès.');
        }
      } else if (data.reponse_ia) {
        // Logique existante pour les messages normaux (string)
        const reponseIA = data.reponse_ia;
        let responseText = '';

        // Si reponse_ia est une string, l'utiliser directement
        if (typeof reponseIA === 'string') {
          responseText = reponseIA;
        } else if (reponseIA.action === 'creer_absence') {
          const entities = reponseIA.entities || reponseIA.entites || {};
          responseText = `✅ J'ai compris : Créer une absence pour ${entities.nom_salarie || 'le salarié'} du ${entities.date_debut || ''} au ${entities.date_fin || ''} (${entities.motif || 'non spécifié'}).`;
        } else if (reponseIA.action === 'lister_planning') {
          const entities = reponseIA.entities || reponseIA.entites || {};
          const salarie = entities.nom_salarie === 'tous' ? 'tous les salariés' : entities.nom_salarie;
          responseText = `📅 Je vais lister le planning de ${salarie || 'tous'} pour ${entities.jour || 'le jour demandé'}.`;
        } else {
          responseText = `J'ai reçu votre demande. Action : ${reponseIA.action || 'non spécifiée'}`;
        }

        // Ajouter la réponse de l'IA à l'historique
        const newIAMessage = {
          text: responseText,
          user: "Archi_Nocibé",
          timestamp: new Date(),
          rawData: reponseIA
        };
        setMessages(prev => [...prev, newIAMessage]);
        
        // Lire la réponse à voix haute si la voix est activée
        if (voiceEnabled && responseText) {
          speak(responseText);
        }
      } else {
        throw new Error('Réponse invalide de l\'API');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      
      let errorMessage = "Désolé, je n'ai pas pu traiter votre demande.";
      
      if (error.response) {
        if (error.response.status === 422) {
          errorMessage = "Je n'ai pas bien compris votre demande. Pouvez-vous reformuler ?";
        } else if (error.response.status === 401) {
          errorMessage = "Erreur d'authentification. Veuillez vous reconnecter.";
        } else if (error.response.data?.erreur) {
          errorMessage = error.response.data.erreur;
        }
      }

      const errorIAMessage = {
        text: errorMessage,
        user: "Archi_Nocibé",
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorIAMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Envoie le message depuis le champ de saisie (utilisé par le bouton Envoyer et Enter)
   */
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) {
      return;
    }
    await handleSendMessageWithText(inputValue.trim());
  };

  /**
   * Gère la soumission du formulaire (Enter)
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  /**
   * Ferme le chat
   */
  const handleClose = () => {
    setIsOpen(false);
    // Arrêter la synthèse vocale si elle est en cours
    stopSpeaking();
    // Arrêter l'écoute si elle est active
    if (isListening) {
      stopListening();
    }
  };

  /**
   * Gère le toggle de la reconnaissance vocale (pour clic simple)
   */
  const handleToggleVoiceRecognition = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  /**
   * Démarre l'écoute au maintien du bouton (push-to-talk)
   */
  const handleMouseDown = () => {
    if (!isListening && !isLoading) {
      startListening();
    }
  };

  /**
   * Arrête l'écoute au relâchement du bouton (push-to-talk)
   */
  const handleMouseUp = () => {
    if (isListening) {
      stopListening();
    }
  };

  /**
   * Gère le touch pour mobile (push-to-talk)
   */
  const handleTouchStart = (e) => {
    e.preventDefault(); // Empêcher le comportement par défaut (zoom, etc.)
    if (!isListening && !isLoading) {
      startListening();
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    if (isListening) {
      stopListening();
    }
  };

  /**
   * Gère le toggle de la synthèse vocale
   */
  const handleToggleVoiceSynthesis = () => {
    if (voiceEnabled) {
      stopSpeaking();
      setVoiceEnabled(false);
    } else {
      setVoiceEnabled(true);
    }
  };

  /**
   * Ouvre le chat
   */
  const handleOpen = () => {
    setIsOpen(true);
  };

  return (
    <>
      {/* Bouton flottant pour ouvrir le chat */}
      {!isOpen && (
        <button 
          className="agent-chat-toggle" 
          onClick={handleOpen}
          aria-label="Ouvrir le chat avec Archi_Nocibé"
          title="Parler à Archi_Nocibé"
        >
          <span className="agent-chat-icon">💬</span>
          <span className="agent-chat-badge">Archi_Nocibé</span>
        </button>
      )}

      {/* Fenêtre de chat */}
      {isOpen && (
        <div className="agent-chat-container">
          <div className="agent-chat-header">
            <div className="agent-chat-header-info">
              <span className="agent-chat-avatar">🤖</span>
              <div>
                <h3>Archi_Nocibé</h3>
                <p className="agent-chat-subtitle">Votre Assistant Nocibé</p>
              </div>
            </div>
            <div className="agent-chat-header-actions">
              <button
                className={`agent-chat-header-voice-btn ${voiceEnabled ? 'agent-chat-header-voice-btn-active' : ''}`}
                onClick={handleToggleVoiceSynthesis}
                disabled={isLoading}
                aria-label={voiceEnabled ? 'Désactiver la lecture vocale' : 'Activer la lecture vocale'}
                title={voiceEnabled ? 'ARCHI ne lira plus les réponses' : 'ARCHI lira les réponses à voix haute'}
              >
                {voiceEnabled ? '🔊' : '🔇'}
              </button>
              <button 
                className="agent-chat-close" 
                onClick={handleClose}
                aria-label="Fermer le chat"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="agent-chat-messages">
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`agent-chat-message ${message.user === 'me' ? 'agent-chat-message-user' : 'agent-chat-message-ia'} ${message.isError ? 'agent-chat-message-error' : ''}`}
              >
                <div className="agent-chat-message-content">
                  {message.text}
                </div>
                <div className="agent-chat-message-time">
                  {message.timestamp.toLocaleTimeString('fr-FR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="agent-chat-message agent-chat-message-ia">
                <div className="agent-chat-message-content agent-chat-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <div className="agent-chat-input-container">
            <div className="agent-chat-input-wrapper">
              <input
                ref={inputRef}
                type="text"
                className="agent-chat-input"
                placeholder={isListening ? "🎤 Écoute en cours... Parlez maintenant" : "Tapez votre message... (ex: pose des vacances pour Jean du 10 au 15 janvier)"}
                value={isListening && transcript ? transcript : inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading || isListening}
              />
              {isSpeechSupported && (
                <button
                  className={`agent-chat-microphone-btn ${isListening ? 'agent-chat-microphone-btn-active' : ''}`}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp} // Arrêter si la souris quitte le bouton
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  onClick={(e) => {
                    // Empêcher le double déclenchement avec onClick
                    e.preventDefault();
                  }}
                  disabled={isLoading}
                  aria-label={isListening ? 'Relâchez pour arrêter l\'écoute' : 'Maintenez pour parler'}
                  title={isListening ? 'Relâchez pour arrêter l\'écoute' : 'Maintenez le bouton pour parler'}
                >
                  {isListening ? '🎤' : '🎙️'}
                </button>
              )}
              <button
                className="agent-chat-send"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                aria-label="Envoyer le message"
              >
                {isLoading ? '⏳' : '➤'}
              </button>
            </div>
            {isSpeechSupported && (
              <div className="agent-chat-voice-controls">
                <button
                  className={`agent-chat-voice-btn ${voiceEnabled ? 'agent-chat-voice-btn-active' : ''}`}
                  onClick={handleToggleVoiceSynthesis}
                  disabled={isLoading}
                  aria-label={voiceEnabled ? 'Désactiver la lecture vocale' : 'Activer la lecture vocale'}
                  title={voiceEnabled ? 'ARCHI ne lira plus les réponses' : 'ARCHI lira les réponses à voix haute'}
                >
                  {voiceEnabled ? '🔊' : '🔇'}
                </button>
              </div>
            )}
          </div>
          {permissionDenied && (
            <div className="agent-chat-permission-warning">
              ⚠️ Permission du microphone refusée. Veuillez autoriser l'accès dans les paramètres de votre navigateur.
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AgentChat;

